import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Crown,
  Check,
  CreditCard,
  Zap,
  Shield,
  Clock,
  AlertCircle,
  Loader2,
  X,
  Tag,
  CheckCircle,
  Star,
} from 'lucide-react';
import { api, PlanInfo, SubscriptionStatus, SubscriptionTier, PaymentProvider, PromoCodeValidationResponse } from '../services/api';
import { fetchPlanPrices, readCachedPlanPrices, PlanPricesResponse } from '../services/planPrices';

const TIER_COLORS: Record<string, { border: string; bg: string; badge: string; button: string }> = {
  free: {
    border: 'border-gray-200 dark:border-gray-700',
    bg: 'bg-gray-100 dark:bg-gray-700',
    badge: 'bg-gray-500',
    button: 'bg-gray-400 cursor-not-allowed',
  },
  pro: {
    border: 'border-blue-500 ring-2 ring-blue-500/20',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    badge: 'bg-blue-600',
    button: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  premium: {
    border: 'border-yellow-500 ring-2 ring-yellow-500/20',
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    badge: 'bg-yellow-500',
    button: 'bg-yellow-500 hover:bg-yellow-600 text-white',
  },
};

export default function Subscription() {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [planPrices, setPlanPrices] = useState<PlanPricesResponse | null>(readCachedPlanPrices);
  const [processing, setProcessing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState<PromoCodeValidationResponse | null>(null);
  const { data: entitlements } = useQuery({
    queryKey: ['entitlements'],
    queryFn: () => api.getEntitlements(),
    staleTime: 30_000,
  });
  const { data: bankerBilling, refetch: refetchBankerBilling } = useQuery({
    queryKey: ['banker-billing-status'],
    queryFn: () => api.getBankerBillingStatus(),
    staleTime: 30_000,
    enabled: entitlements?.investment_banking_gate === true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansData, subData] = await Promise.all([
        api.getAvailablePlans(),
        api.getSubscriptionStatus(),
      ]);
      setPlans(plansData.plans);
      setSubscription(subData);
    } catch (err) {
      setError('Failed to load subscription data');
      console.error(err);
    } finally {
      setLoading(false);
    }
    fetchPlanPrices().then(setPlanPrices).catch(() => {});
  };

  const handleSubscribe = async (tier: SubscriptionTier) => {
    setProcessing(true);
    setError(null);

    try {
      const provider: PaymentProvider = 'stripe';
      const response = await api.createCheckoutSession({ tier, provider });
      window.location.href = response.checkout_url;
    } catch (err) {
      setError('Failed to create checkout session');
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    setProcessing(true);
    try {
      await api.cancelSubscription(cancelReason);
      setShowCancelModal(false);
      await loadData();
    } catch (err) {
      setError('Failed to cancel subscription');
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const handleBankerCheckout = async () => {
    setProcessing(true);
    setError(null);
    try {
      const response = await api.createBankerCheckout();
      window.location.assign(response.checkout_url);
    } catch {
      setError('Failed to start Banker checkout');
    } finally {
      setProcessing(false);
    }
  };

  const handleBankerCancel = async () => {
    setProcessing(true);
    try {
      await api.cancelBankerSubscription();
      await refetchBankerBilling();
    } catch {
      setError('Failed to cancel Banker subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleBankerManage = async () => {
    setProcessing(true);
    setError(null);
    try {
      const response = await api.manageBankerSubscription();
      window.location.assign(response.checkout_url);
    } catch {
      setError('Failed to open Banker payment management');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString()}`;
  };

  const livePriceFor = (plan: PlanInfo): number => {
    const tier = plan.tier as keyof PlanPricesResponse;
    return planPrices?.[tier] ? planPrices[tier].amount_cents / 100 : plan.price;
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;

    setPromoValidating(true);
    try {
      const result = await api.validatePromoCode(promoCode);
      setPromoResult(result);
    } catch (err) {
      setPromoResult({
        valid: false,
        code: promoCode,
        message: 'Failed to validate promo code',
      });
    } finally {
      setPromoValidating(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode('');
    setPromoResult(null);
  };

  const getDiscountedPrice = (planPrice: number) => {
    if (!promoResult?.valid || !promoResult.discount_type) return null;

    if (promoResult.discount_type === 'percentage') {
      const discount = planPrice * ((promoResult.discount_value || 0) / 100);
      return planPrice - discount;
    } else {
      return Math.max(0, planPrice - (promoResult.discount_value || 0));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const currentTier = subscription?.tier || 'free';
  const isPaid = currentTier === 'pro' || currentTier === 'premium';

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Crown className="h-7 w-7 text-yellow-500" />
          Subscription
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage your subscription and billing
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Current Subscription Status */}
      {subscription && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Current Subscription
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${isPaid ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                <Crown className={`h-5 w-5 ${isPaid ? 'text-yellow-600' : 'text-gray-500'}`} />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Plan</p>
                <p className="font-semibold text-gray-900 dark:text-white capitalize">
                  {currentTier}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {isPaid ? 'Expires' : 'Status'}
                </p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {isPaid ? formatDate(subscription.expires_at) : currentTier === 'free' ? '7-day AI trial' : 'Active'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <Zap className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">AI Access</p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {currentTier === 'free' ? 'Rate limited (trial)' : 'Rate limited'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {currentTier === 'premium' ? '3 calls/5min (EA) · 1/5min (dashboard)'
                    : currentTier === 'pro' ? '3 calls/15min (EA) · 1/hour (dashboard)'
                    : '1 call/hour (EA & dashboard)'}
                </p>
              </div>
            </div>
          </div>

          {isPaid && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {subscription.will_renew
                  ? 'Your subscription will automatically renew'
                  : 'Your subscription will not renew'}
              </p>
              <button
                onClick={() => setShowCancelModal(true)}
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                Cancel Subscription
              </button>
            </div>
          )}
        </div>
      )}

      {bankerBilling && (
        <div className="bg-slate-800 rounded-xl shadow-md p-6 border border-teal-400/20">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Investment Banker add-on</h2>
              <p className="mt-1 text-sm text-slate-400">{bankerBilling.display_price} · {bankerBilling.trial_days}-day free trial</p>
              <p className="mt-2 text-sm text-slate-300 capitalize">Status: {bankerBilling.investment_banking.state.replace('_', ' ')}</p>
            </div>
            {['renewal_due', 'overdue_disabled'].includes(bankerBilling.investment_banking.state) ? (
              <button type="button" onClick={handleBankerManage} disabled={processing} className="min-h-11 rounded-lg bg-teal-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-teal-400 disabled:opacity-50">{bankerBilling.investment_banking.state === 'overdue_disabled' ? 'Pay now' : 'Manage payment'}</button>
            ) : bankerBilling.investment_banking.active ? (
              <button type="button" onClick={handleBankerCancel} disabled={processing} className="min-h-11 rounded-lg border border-red-400/50 px-4 py-2 text-sm font-medium text-red-300 hover:bg-red-400/10 disabled:opacity-50">Cancel Banker add-on</button>
            ) : (
              <button type="button" onClick={handleBankerCheckout} disabled={processing} className="min-h-11 rounded-lg bg-teal-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-teal-400 disabled:opacity-50">Subscribe to Banker</button>
            )}
          </div>
        </div>
      )}

      {/* Promo Code Section */}
      {!isPaid && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="h-5 w-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Have a promo code?
            </h2>
          </div>

          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase());
                  if (promoResult) setPromoResult(null);
                }}
                placeholder="Enter promo code"
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              {promoResult?.valid && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
              )}
            </div>
            <button
              onClick={handleValidatePromo}
              disabled={!promoCode.trim() || promoValidating}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {promoValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Apply
            </button>
            {promoResult && (
              <button
                onClick={clearPromoCode}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {promoResult && (
            <div
              className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${
                promoResult.valid
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
              }`}
            >
              {promoResult.valid ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              <span>
                {promoResult.message}
                {promoResult.valid && promoResult.discount_type && (
                  <span className="font-medium">
                    {' '}
                    ({promoResult.discount_type === 'percentage'
                      ? `${promoResult.discount_value}% off`
                      : `${formatCurrency(promoResult.discount_value || 0)} off`})
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Pricing Plans — render all from API */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const colors = TIER_COLORS[plan.tier] || TIER_COLORS.free;
          const isCurrent = currentTier === plan.tier;
          const isFreePlan = plan.tier === 'free';
          const effectivePrice = livePriceFor(plan);
          const discountedPrice = getDiscountedPrice(effectivePrice);

          return (
            <div
              key={plan.tier}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border-2 ${colors.border} relative`}
            >
              {plan.tier === 'premium' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Star className="h-3 w-3" /> Best Value
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                <div className="mt-2">
                  {promoResult?.valid && !isFreePlan && discountedPrice !== null ? (
                    <>
                      <span className="text-2xl line-through text-gray-400 dark:text-gray-500">
                        {formatCurrency(effectivePrice)}
                      </span>
                      <span className="text-4xl font-bold text-green-600 dark:text-green-400 ml-2">
                        {formatCurrency(discountedPrice)}
                      </span>
                    </>
                  ) : (
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(effectivePrice)}
                    </span>
                  )}
                  <span className="text-gray-500 dark:text-gray-400">/{plan.interval}</span>
                </div>
                {promoResult?.valid && !isFreePlan && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1 flex items-center justify-center gap-1">
                    <Tag className="h-3 w-3" />
                    Promo code applied!
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(plan.tier)}
                disabled={processing || isCurrent || isFreePlan}
                className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  isCurrent || isFreePlan
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                    : colors.button
                }`}
              >
                {processing ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : isCurrent ? (
                  'Current Plan'
                ) : isFreePlan ? (
                  'Free'
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    {isPaid ? 'Switch Plan' : 'Subscribe Now'}
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Features Comparison */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Why Upgrade?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <Zap className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">More EA Charts</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Connect up to 10 charts with Premium
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <Clock className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">Faster AI Calls</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                3 calls per 5 min vs 1 per hour
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
              <Star className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">Self-Learning</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                AI learns from your feedback to improve
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
              <Shield className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">Priority Support</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                24/7 support with Premium plan
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          Secure payments powered by
        </p>
        <div className="flex items-center justify-center gap-6">
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <CreditCard className="h-5 w-5" />
            <span className="font-medium">Stripe</span>
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Cancel Subscription
              </h3>
              <button
                onClick={() => setShowCancelModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to cancel? You'll lose access to paid features
              at the end of your billing period and revert to the free trial.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reason for cancelling (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                rows={3}
                placeholder="Help us improve..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={processing}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2"
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
